import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { RefreshCw } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAttributionData, type DateRange } from "@/hooks/useAttributionData";
import { AttributionKPIStrip } from "./AttributionKPIStrip";
import { CampaignsTab } from "./CampaignsTab";
import { SettersTab } from "./SettersTab";
import { ClosersTab } from "./ClosersTab";
import { useClients } from "@/hooks/useClients";
import { useRole } from "@/contexts/RoleContext";
import { useStageMapping } from "@/hooks/useStageMapping";

type Preset = "7" | "30" | "90" | "all";

function presetToRange(preset: Preset): DateRange {
  const to = new Date();
  const from = new Date();
  if (preset === "7") from.setDate(to.getDate() - 7);
  else if (preset === "30") from.setDate(to.getDate() - 30);
  else if (preset === "90") from.setDate(to.getDate() - 90);
  else from.setFullYear(2000);
  return { from, to };
}

interface AttributionPageProps {
  fixedClientId?: string;
}

export default function AttributionPage({ fixedClientId }: AttributionPageProps) {
  const { role, clientId } = useRole();
  // useRole() returns 'admin' | 'client' — NOT raw Supabase role strings
  const isAdmin = role === "admin";
  const { clients } = useClients();
  const { mappings } = useStageMapping(fixedClientId ?? clientId);
  const [preset, setPreset] = useState<Preset>("all");
  const [selectedClientId, setSelectedClientId] = useState<string>("all");
  const dateRange = useMemo(() => presetToRange(preset), [preset]);
  const activeClientId = fixedClientId ?? (selectedClientId === "all" ? undefined : selectedClientId);
  const { kpis, campaigns, setters, closers, loading, error, refetch } = useAttributionData(dateRange, activeClientId);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Attribution</h1>
          <p className="text-sm text-muted-foreground mt-1">Track lead → demo → close across campaigns, setters, and closers</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {isAdmin && !fixedClientId && (
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger className="w-40 h-9 text-sm" data-tour-id="attribution-client-select"><SelectValue placeholder="All Clients" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-0.5 bg-muted rounded-lg p-1" data-tour-id="attribution-date-range">
            {(["7", "30", "90", "all"] as Preset[]).map(p => (
              <button key={p} onClick={() => setPreset(p)} className={`px-3 py-1 text-xs font-medium rounded transition-colors ${preset === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>
                {p === "all" ? "All" : `${p}d`}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={refetch} disabled={loading} className="h-9 w-9 p-0">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>
      {error && <div className="mb-4 p-3 rounded-lg border border-destructive/50 bg-destructive/10 text-sm text-destructive">Failed to load attribution data: {error}</div>}
      <div data-tour-id="attribution-kpi-strip">
        <AttributionKPIStrip kpis={kpis} loading={loading} />
      </div>
      {mappings.length === 0 && !loading && (
        <div className="text-center py-4 text-muted-foreground text-sm">
          Map GHL pipeline stages in Client Integrations to see attribution metrics.
        </div>
      )}
      <Tabs defaultValue="campaigns" data-tour-id="attribution-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="setters">Setters</TabsTrigger>
          <TabsTrigger value="closers">Closers</TabsTrigger>
        </TabsList>
        <TabsContent value="campaigns"><CampaignsTab campaigns={campaigns} loading={loading} /></TabsContent>
        <TabsContent value="setters"><SettersTab setters={setters} loading={loading} /></TabsContent>
        <TabsContent value="closers"><ClosersTab closers={closers} loading={loading} /></TabsContent>
      </Tabs>
    </motion.div>
  );
}
